function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// ----------------------------------------------------------------------------
// Product Gallery Module: Feature Slider
// ----------------------------------------------------------------------------
// The FeatureSlider class manages the product image gallery as a slider
// - It synchronizing the main feature slider with the thumbnail slider (if present)
// - It updates the displayed image based on variant selection and user interactions.
// - Depends on Swiper (swiperjs.com) for the slider functionality.
// ----------------------------------------------------------------------------
// Event Listeners:
// - 'productForm:gallery:init'    → Listens for the gallery initalization to begin
//                                   initalizing the feature and thumbnail sliders
// - 'productForm:variant:changed' → Updates the displayed image when a different
//                                   variant is selected.
// ----------------------------------------------------------------------------
// Event Triggers:
// - 'productForm:gallery:featureSlider:changed' → Fires when the main feature
//                                                 slider changes slides.
// - 'productForm:gallery:featureSlider:ready'   → Fires when the feature slider
//                                                 is fully initialized.
// ----------------------------------------------------------------------------
// Gotchas:
// - The feature slider initializes *after* the thumbnail slider to ensure proper linking.
// - The focus state of interactive elements (e.g., buttons, iframes, inputs) inside slides
//   is toggled dynamically for accessibility.
// - The variant change handler prioritizes matching the most specific available variant
//   based on selected options (e.g., Color+Waist+Length). If no exact match is found,
//   it will attempt to find a match based on only Option1+Option2. If a match still isn't
//   found, it will attempt matching only Option1. Otherwise, it defaults to the first slide.
// - Consider the image selection logic when defining the option hirearchy accordingly,
//   otherwise override the `focuSlideOnNearestMatch` method to customize the behavior.
// - The thumbnail slider uses a different layout (horizontal by default, vertical for wider screens).
// - If the expected thumbnail element isn't found, the slide change logic may not function correctly.
// - Once the feature slider is inialited, the Media Gallery loading state is disabled.
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Helpers
import Swiper from 'swiper';
import { Navigation, Thumbs } from 'swiper/modules';
import iamotaEventHandler from 'scripts/helpers/iamota-event-handler';
import { toggleInnerFocus } from 'scripts/helpers/utilities';
import Debug from 'scripts/libs/debug';

// Styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/free-mode';
import 'swiper/css/a11y';
import 'swiper/css/keyboard';
import 'swiper/css/thumbs';

// -------------------------------------
// Feature Slider
// -------------------------------------

class FeatureSlider {
  constructor(iamProductGallery) {
    _defineProperty(this, "FOCUSABLE_ELEMENTS", ['iframe', 'input', 'select', 'option', 'textarea', 'button', 'video', 'label', 'a', '.plyr', 'model-viewer']);
    // Reference to parent
    this.iamProductGallery = iamProductGallery;

    // Register Events
    iamotaEventHandler.subscribe('productForm:variant:changed', this.onVariantChange.bind(this), {
      context: this.iamProductGallery.iamForm,
      source: "featureSlider.js"
    });

    // Initialize Gallery Sliders
    this.thumbnailSlider = this.initThumbnailSlider();
    this.initFeatureSlider(this.thumbnailSlider); // init after thumbnails so they can be linked);

    if (this.thumbnailSlider) {
      this.onThumbnailFocus();
    }
  }

  /**
   * On Slide Change
   */
  onSlideChange(swiper) {
    iamotaEventHandler.trigger('productForm:gallery:featureSlider:changed', {
      swiper
    }, {
      context: this.iamProductGallery
    });
    toggleInnerFocus(swiper, this.FOCUSABLE_ELEMENTS);
  }
  onThumbnailFocus() {
    this.thumbnailSlider.slides.forEach((slide, index) => {
      slide.addEventListener('focus', () => {
        this.featureSlider.slideTo(index);
      });
    });
  }

  /**
   * On Variant Change
   */
  onVariantChange(_ref) {
    var {
      variant
    } = _ref;
    this.focusSlideOnSelection(variant);
  }

  /**
   * Init the thumbnail slider
   */
  initThumbnailSlider() {
    var thumbnailContainer = this.iamProductGallery.querySelector('.carousel--product-thumbnails');
    var thumbnailSlides = (thumbnailContainer === null || thumbnailContainer === void 0 ? void 0 : thumbnailContainer.querySelectorAll('.swiper-slide')) || [];
    if (!thumbnailContainer || thumbnailSlides.length <= 1) {
      return null;
    }
    return new Swiper(thumbnailContainer, {
      modules: [Navigation],
      slidesPerView: 4,
      spaceBetween: 8,
      freeMode: true,
      watchSlidesVisibility: true,
      watchSlidesProgress: true,
      slideToClickedSlide: true,
      navigation: true,
      a11y: false,
      multipleActiveThumbs: false,
      keyboard: {
        enabled: false
      },
      breakpoints: {
        768: {
          direction: 'vertical',
          slidesPerView: 'auto',
          spaceBetween: 10
        }
      }
    });
  }

  /**
   * Init the feature slider
   */
  initFeatureSlider(thumbnailSlider) {
    var featureContainer = this.iamProductGallery.querySelector('.carousel--product-gallery');
    var prevButton = this.iamProductGallery.querySelector('.swiper-button-prev-product');
    var nextButton = this.iamProductGallery.querySelector('.swiper-button-next-product');
    var featureSlides = (featureContainer === null || featureContainer === void 0 ? void 0 : featureContainer.querySelectorAll('.swiper-slide')) || [];
    var swiperConfig = {
      modules: [Navigation, Thumbs],
      spaceBetween: 0,
      updateOnImagesReady: true,
      on: {
        init: function (swiper) {
          Debug.extreme('productForm:gallery:featureSlider:init', swiper);
          this.featureSlider = swiper;
          this.onFeatureSliderReady(swiper);
          toggleInnerFocus(swiper, this.FOCUSABLE_ELEMENTS);
        }.bind(this),
        slideChange: function (swiper) {
          this.onSlideChange(swiper);
        }.bind(this)
      }
    };
    if (featureSlides.length > 1 && prevButton && nextButton) {
      swiperConfig.navigation = {
        nextEl: nextButton,
        prevEl: prevButton
      };
    }
    if (thumbnailSlider) {
      swiperConfig.thumbs = {
        swiper: thumbnailSlider
      };
    }
    return new Swiper(featureContainer, swiperConfig);
  }
  onFeatureSliderReady(swiper) {
    Debug.extreme('productForm:gallery:onFeatureSliderReady', swiper);

    // Reference to the feature slider
    iamotaEventHandler.trigger('productForm:gallery:featureSlider:ready', {
      swiper
    }, {
      context: this.iamProductGallery
    });

    // Update the slider image to the initial variant
    this.focusSlideOnSelection(this.iamProductGallery.iamForm.getCurrentVariant());

    // Disable loading state
    this.iamProductGallery.setLoading(false);
  }
  focusSlideOnSelection(variant) {
    Debug.extreme('productForm:gallery:focusSlideOnSelection', variant);
    if (variant !== null && variant !== void 0 && variant.featured_media) {
      this.focusSlideOnMedia(variant.featured_media.id);
    } else {
      this.focusSlideOnNearestMatch();
    }
  }
  focusSlideOnMedia(mediaId) {
    var _mediaWrapper$closest;
    Debug.extreme('productForm:gallery:focusSlideOnSelection', mediaId);
    var targetMediaId = "".concat(this.iamProductGallery.sectionId, "-").concat(mediaId);
    var mediaWrapper = this.iamProductGallery.querySelector(".product-single__media-wrapper[data-media-id='".concat(targetMediaId, "']"));
    var slideIndex = parseInt(mediaWrapper === null || mediaWrapper === void 0 || (_mediaWrapper$closest = mediaWrapper.closest('.swiper-slide')) === null || _mediaWrapper$closest === void 0 ? void 0 : _mediaWrapper$closest.getAttribute('data-index'), 10);
    if (!isNaN(slideIndex)) {
      this.featureSlider.slideTo(slideIndex);
    }
  }
  focusSlideOnNearestMatch() {
    Debug.extreme('productForm:gallery:focusSlideOnNearestMatch');
    var options = this.iamProductGallery.iamForm.getCurrentOptions();

    // Check options from most specific to least specific
    // e.g. Check for Color+Waist+Length, Color+Waist, Color
    for (var i = options.length - 1; i > 0; i--) {
      var partialOptions = options.slice(0, i);
      var closestMatch = this.findVariantWithOptions(partialOptions);
      if (closestMatch && closestMatch.featured_media) {
        //console.log('Found something close...', partialOptions, closestMatch);
        this.focusSlideOnMedia(closestMatch.featured_media.id);
        return;
      }
    }
  }

  /**
   * Find a variant that matches the given options
   * @returns {variantObject}
   */
  findVariantWithOptions(options) {
    return this.iamProductGallery.iamForm.productData.variants.find(variant => options.every((option, index) => variant.options[index] === option.value));
  }
}
var featureSliders = [];
iamotaEventHandler.subscribe('productForm:gallery:init', _ref2 => {
  var {
    iamProductGallery
  } = _ref2;
  featureSliders.push(new FeatureSlider(iamProductGallery));
}, {
  source: "featureSlider.js"
});
export default featureSliders;